<template>
  <div class="relative member-activity-report flex flex-col justify-center items-center">
    <div class="w-full">
      <report-header
        :error.sync="error"
        :report="report"
        :range="range"
        @getReport="getReport"
      >
        <template #title>
          new member activity report
        </template>
        <template #subtitle>
          analyze how newly added members are shopping in their first 3 months
        </template>
      </report-header>
      <template v-if="report">
        <div class="flex justify-center items-center w-full text-center mt-4 pb-6">
          <div class="w-1/6 flex-1 md:flex-none">
            <loading-icon v-if="loadingReport" class="mx-auto" />
            <template v-else-if="totalNewMembers || totalNewMembers === 0">
              <div class="text-lg font-secondary">new members</div>
              <div class="text-2xl">{{totalNewMembers}}</div>
            </template>
          </div>
          <div class="w-1/6 flex-1 md:flex-none">
            <loading-icon v-if="loadingReport" class="mx-auto" />
            <template v-else-if="totalVisits || totalVisits === 0">
              <div class="text-lg font-secondary">total visits</div>
              <div class="text-2xl">{{totalVisits}}</div>
            </template>
          </div>
          <div class="w-1/6 flex-1 md:flex-none">
            <loading-icon v-if="loadingReport" class="mx-auto" />
            <template v-else-if="totalSpend || totalSpend === 0">
              <div class="text-lg font-secondary">total spend</div>
              <div class="text-2xl">${{totalSpend}}</div>
            </template>
          </div>
        </div>
        <div class="w-full border-0 border-b border-blue-light border-solid" />
      </template>
    </div>
    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
      <loading-icon />
    </div>
    <div v-else-if="report" class="flex justify-start items-end w-full">
      <div class="w-40 text-right mb-2">
        <div class="text-xl font-secondary my-4">total visits</div>
        <div class="text-xl font-secondary my-4">total spend</div>
        <div class="text-xl font-secondary my-4">average visits</div>
        <div class="text-xl font-secondary my-4">average spend</div>
      </div>
      <sb-custom-table
        :rows="tableRows"
        :columns="tableColumns"
        disableTableSort
      />
    </div>
  </div>
</template>

<script>
import { subDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { DateRange, SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { reportsAPI } from '../../services/api/reportsAPI';
import { commafyNumber } from "../../utilities";
import { mapGetters } from 'vuex';
import ReportHeader from "./ReportHeader.vue";

export default {
  name: "NewMemberActivityReport",
  components: {
    DateRange,
    SbCustomTable,
    Pagination,
    LoadingIcon,
    ReportHeader,
  },
  data() {
    return {
      range: null,
      report: null,
      tableRows: [],
      loadingReport: false,
      error: {},
      tableColumns: []
    }
  },
  async created(){
    await this.$store.dispatch('LOAD_CURRENT_MERCHANT');
    this.range = this.defaultDateRange();
  },
  computed: {
    totalNewMembers() {
      return commafyNumber(this.report?.output_data.totals.new_members) || 0;
    },
    totalSpend() {
      return new Intl.NumberFormat('en-US').format(Math.round(this.report?.output_data.totals.total_spend)) || 0;
    },
    totalVisits() {
      return commafyNumber(this.report?.output_data.totals.total_visits) || 0;
    },
    ...mapGetters(['merchantTimeZone'])
  },
  methods: {
    defaultDateRange() {
      const currentDate = utcToZonedTime(new Date(), this.merchantTimeZone);
      const minimumDate = subDays(currentDate, 90);

      return {
        min: minimumDate,
        start: minimumDate,
        max: subDays(currentDate, 1),
        end: subDays(currentDate, 1)
      }
    },
    formatReportData() {
      const results = this.report.output_data.months;

      if (results.length > 0) {
        const columns = [];
        const rows = results.map((month, index) => {
          columns.push({key: `column${index + 1}`, value:''})
          return {
          ...month,
          total_spend: `$${commafyNumber(Math.round(month.total_spend))}`,
          average_spend: `$${commafyNumber(Math.round(month.average_spend))}`
        }});
        this.tableColumns = columns;
        this.tableRows = Object.keys(rows[0]).map((key) => {
          let row = {};
          for (let i = 0; i < rows.length; i++) {
            row[`column${i + 1}`] = rows[i][key];
          }
          return row;
        });

        const monthsRow = this.tableRows[0],
              totalVisitsRow = this.tableRows[1],
              totalSpendRow = this.tableRows[2],
              averageVisitsRow = this.tableRows[3],
              averageSpendRow = this.tableRows[4];

        this.tableRows = [
          monthsRow,
          totalVisitsRow,
          totalSpendRow,
          averageVisitsRow,
          averageSpendRow,
        ];
      }
    },
    createNewReport() {
      return reportsAPI.createReport('Reports::NewMemberActivityReport', this.range.start, this.range.end);
    },
    fetchExistingReport() {
      return reportsAPI.getReportById(this.report.id, 1);
    },
    async getReport(newReport) {
      this.loadingReport = true;
      try {
        const { report } = !this.report?.id || (this.report.id && newReport) ? await this.createNewReport() : await this.fetchExistingReport();
        this.report = report;
        this.formatReportData();
      } catch(error){
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingReport = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.member-activity-report {
  &::v-deep {
    table {
      tr {
        td {
          text-transform: lowercase;
        }
        &:first-child {
          td {
            font-family: "AileronLight";
            font-size: 1.25rem!important;
          }
        }
      }
    }
  }
}
</style>
